import React, { useState } from "react";
import { ChatPanel } from "llmasaservice-ui";
import { useUser } from "../../contexts/useUser";
import "../../../node_modules/llmasaservice-ui/dist/index.css";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { fetchLLMGroups } from "../../dataaccess/fetchLLMGroups";
import { fetchLLMModels } from "../../dataaccess/fetchLLMModels";
import { LLMEmailCaptureMode } from "../../API";
import { useNavigate } from "react-router-dom";

/**
 * Admin LLM Test panel: allows an admin to test the project chat service while spoofing a customer email.
 * Re-uses logic from ChatTest but adds an override email field and always passes a customer object to ChatPanel.
 */
const AdminLLMTest: React.FC = () => {
  const { project, user } = useUser();
  const navigate = useNavigate();
  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [createConversation, setCreateConversation] = useState<boolean>(false);
  const [customerEmailCaptureMode, setCustomerEmailCaptureMode] =
    useState<LLMEmailCaptureMode>(LLMEmailCaptureMode.HIDE);
  const [overrideEmail, setOverrideEmail] = useState<string>(user?.email || "");

  const { data: groupsData, isLoading: groupsLoading, isError: groupsError } = useQuery({
    queryKey: ["groups"],
    queryFn: () => fetchLLMGroups(project?.id ?? ""),
    enabled: !!project?.id,
  });

  const {
    data: modelsData,
    isLoading: modelsLoading,
    isError: modelsError,
  } = useQuery({
    queryKey: ["models"],
    queryFn: () => fetchLLMModels(),
    enabled: true,
  });

  if (groupsLoading || modelsLoading) return <div>Loading...</div>;
  if (groupsError || modelsError) return <div>Error loading data...</div>;

  const groups = groupsData?.data?.groupsByProjectId?.items ?? [];
  const models = modelsData?.data?.listLLMModels?.items ?? [];

  // Group models by their default service group and vendor for organized selection
  const modelsByServiceGroup = models.reduce((acc, model) => {
    const serviceGroup = model?.defaultServiceGroup || "Other";
    if (!acc[serviceGroup]) acc[serviceGroup] = [];
    acc[serviceGroup].push(model);
    return acc;
  }, {} as Record<string, any[]>);

  const modelsByVendor = models.reduce((acc, model) => {
    const vendor = model?.vendor || "Unknown";
    if (!acc[vendor]) acc[vendor] = [];
    acc[vendor].push(model);
    return acc;
  }, {} as Record<string, any[]>);

  const options = [
    { id: "", label: "Default group", type: "default" },
    { id: "group:standard", label: "Standard Model Group", type: "service-group" },
    { id: "group:premium", label: "Premium Model Group", type: "service-group" },
    { id: "group:reasoning", label: "Reasoning Model Group", type: "service-group" },
    { id: "group:superpremium", label: "Super Premium Model Group", type: "service-group" },
    { id: "group:supersmall", label: "Super Small Model Group", type: "service-group" },
    { id: "openrouter:standard", label: "OpenRouter:Standard Model Group", type: "service-group" },
    { id: "openrouter:premium", label: "OpenRouter:Premium Model Group", type: "service-group" },
    ...(groups?.map((group) => ({
      id: group?.id ?? "",
      label: "Custom group: " + (group?.name ?? ""),
      type: "custom-group",
    })) ?? []),
    { id: "separator-service", label: "— Models by Service Group —", type: "separator", disabled: true },
    ...Object.entries(modelsByServiceGroup)
      .sort(([a], [b]) => a.localeCompare(b))
      .flatMap(([serviceGroup, groupModels]) => [
        { id: `header-service-${serviceGroup}`, label: `${serviceGroup} Models`, type: "header", disabled: true },
        ...groupModels
          .sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))
          .map((model) => ({
            id: `model:${model?.id}`,
            label: `  ${model?.name} (${model?.vendor}${model?.host && model?.host !== model?.vendor ? ` via ${model?.host}` : ""})`,
            type: "model",
          })),
      ]),
    { id: "separator-vendor", label: "— Models by Vendor —", type: "separator", disabled: true },
    ...Object.entries(modelsByVendor)
      .sort(([a], [b]) => a.localeCompare(b))
      .flatMap(([vendor, vendorModels]) => [
        { id: `header-vendor-${vendor}`, label: `${vendor} Models`, type: "header", disabled: true },
        ...vendorModels
          .sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))
          .map((model) => ({
            id: `model:${model?.id}`,
            label: `  ${model?.name}${model?.host && model?.host !== model?.vendor ? ` (via ${model?.host})` : ""}`,
            type: "model",
          })),
      ]),
  ];

  let chatUrl = "https://chat.llmasaservice.io/";
  let publicAPIUrl = "https://api.llmasaservice.io"; // (kept for potential future use / parity with ChatTest)
  if (
    window.location.hostname === "localhost" ||
    window.location.hostname === "dev.llmasaservice.io"
  ) {
    chatUrl = "https://chat.llmasaservice.io/dev";
    publicAPIUrl = "https://8ftw8droff.execute-api.us-east-1.amazonaws.com/dev";
  }

  const THEME_KEY = "_llmasaservice_theme";
  const userThemePreference = localStorage.getItem(THEME_KEY) || "dark";

  return (
    <Box margin={2}>
      <Alert
        severity="info"
        sx={{ mb: 2 }}
        action={
          <Button color="inherit" size="small" onClick={() => navigate("/chat/batch")}>
            Open Batch Test
          </Button>
        }
      >
        This page is the interactive streaming chat tester. For true vendor-discounted batch calls, use the dedicated Vendor Batch Test page.
      </Alert>

      <Paper elevation={3} style={{ borderRadius: 3, border: "1px solid #444" }}>
        <Box padding={2} margin={1} display="flex" flexWrap="wrap" gap={2}>
          <FormControl size="small" sx={{ minWidth: 300 }}>
            <InputLabel id="group-select-label">Service Group / Model</InputLabel>
            <Select
              labelId="group-select-label"
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              label="Service Group / Model"
              size="small"
            >
              {options.map((option) => (
                <MenuItem
                  key={option.id}
                  value={option.id}
                  disabled={option.disabled}
                  style={{
                    fontWeight: option.type === "header" ? "bold" : "normal",
                    fontSize:
                      option.type === "header"
                        ? "0.9em"
                        : option.type === "separator"
                        ? "0.85em"
                        : "1em",
                    color:
                      option.type === "separator" || option.type === "header"
                        ? "#666"
                        : "inherit",
                    backgroundColor:
                      option.type === "separator" || option.type === "header"
                        ? "#f5f5f5"
                        : "inherit",
                    paddingLeft: option.type === "model" ? "32px" : "16px",
                    fontStyle: option.type === "separator" ? "italic" : "normal",
                    cursor: option.disabled ? "default" : "pointer",
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={createConversation}
                onChange={(e) => setCreateConversation(e.target.checked)}
              />
            }
            label="Save conversation?"
          />

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="email-capture-label">Email capture mode</InputLabel>
            <Select
              labelId="email-capture-label"
              value={customerEmailCaptureMode}
              onChange={(e: any) => setCustomerEmailCaptureMode(e.target.value)}
              label="Email capture mode"
              size="small"
            >
              <MenuItem value={LLMEmailCaptureMode.HIDE}>Hide</MenuItem>
              <MenuItem value={LLMEmailCaptureMode.OPTIONAL}>Optional</MenuItem>
              <MenuItem value={LLMEmailCaptureMode.REQUIRED}>Required</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Test as user email"
            size="small"
            value={overrideEmail}
            onChange={(e) => setOverrideEmail(e.target.value)}
            sx={{ minWidth: 260 }}
            placeholder="user@example.com"
          />
        </Box>
      </Paper>

      <ChatPanel
        project_id={project?.id ?? ""}
        initialMessage={`Hello. Testing LLM services for project ${project?.name}. This chat is acting as ${overrideEmail || user?.email || "unknown user"}.`}
        theme={userThemePreference === "light" ? "light" : "dark"}
        height="80vh"
        width="100%"
        url={chatUrl}
        hideInitialPrompt={false}
        service={selectedGroup}
        createConversationOnFirstChat={createConversation}
        customerEmailCaptureMode={customerEmailCaptureMode}
        customer={{
          customer_id: (overrideEmail || user?.email || "").toLowerCase(),
          customer_name: overrideEmail || user?.email || "",
        }}
        key={overrideEmail + selectedGroup + customerEmailCaptureMode}
      />
    </Box>
  );
};

export default AdminLLMTest;
